import {api} from './api';
import {Entry, PropertyDefinition, RepositoryByIdResponse} from '../types';
import {entriesDeleteMany, entriesUpdateMany, propertyDefinitionsAddMany} from '../containers/contentSlice';

/** Required for reorder: property defs may exist only in RTK cache, not in `content.entries`. */
export type PropertyDefinitionsUpdateManyArg = {
	repositoryId: string;
	records: Partial<Entry>[];
};

function repositoryIdForPropertyDefinition(getState: () => unknown, propertyDefinitionId: string): string | undefined {
	const state = getState() as {content: {entries: Entry[]}};
	const entry = state.content.entries.find((e) => e.id === propertyDefinitionId);
	return entry?.parentId ?? undefined;
}

export type PropertyDefinitionUpdateArg = {
	id: string;
	record: Record<string, unknown>;
	repositoryId?: string;
};

export type PropertyDefinitionDeleteArg = {
	id: string;
	repositoryId?: string;
};

export const propertyDefinitionsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		propertyDefinitionCreate: builder.mutation<Entry, PropertyDefinition>({
			query: (entry: PropertyDefinition) => ({
				url: 'property-definitions',
				method: 'POST',
				body: {record: entry},
			}),
			async onQueryStarted(arg, {dispatch, queryFulfilled}) {
				dispatch(propertyDefinitionsAddMany([arg]));
				try {
					await queryFulfilled;
				} catch (err) {
					// rollback
					dispatch(entriesDeleteMany([arg.id]));
					return;
				}
				if (arg.parentId) {
					dispatch(api.util.invalidateTags([{type: 'Repository' as const, id: arg.parentId}]));
				}
			},
		}),
		propertyDefinitionDelete: builder.mutation<{deletedIds: string[]}, PropertyDefinitionDeleteArg>({
			query: ({id}) => ({
				url: `property-definitions/${id}`,
				method: 'DELETE',
			}),
			async onQueryStarted({id, repositoryId: repositoryIdHint}, {dispatch, queryFulfilled, getState}) {
				const repositoryId = repositoryIdHint ?? repositoryIdForPropertyDefinition(getState, id);
				dispatch(entriesDeleteMany([id]));
				try {
					await queryFulfilled;
				} catch {
					return;
				}
				if (repositoryId) {
					dispatch(api.util.invalidateTags([{type: 'Repository' as const, id: repositoryId}]));
				}
			},
		}),
		propertyDefinitionUpdate: builder.mutation<Entry, PropertyDefinitionUpdateArg>({
			query: ({id, record}) => ({
				url: `property-definitions/${id}`,
				method: 'PUT',
				body: {record},
			}),
			async onQueryStarted({id, record, repositoryId: repositoryIdHint}, {dispatch, queryFulfilled, getState}) {
				dispatch(entriesUpdateMany([{id, ...record}]));
				try {
					await queryFulfilled;
				} catch {
					return;
				}
				const repositoryId = repositoryIdHint ?? repositoryIdForPropertyDefinition(getState, id);
				if (repositoryId) {
					dispatch(api.util.invalidateTags([{type: 'Repository' as const, id: repositoryId}]));
				}
			},
		}),
		propertyDefinitionsUpdateMany: builder.mutation<void, PropertyDefinitionsUpdateManyArg>({
			query: ({records}) => ({
				url: 'property-definitions',
				method: 'PUT',
				body: {records},
			}),
			// This part takes care of local optimistic update (after reordering is committed).
			async onQueryStarted({repositoryId, records}, {dispatch, queryFulfilled}) {
				const rankById = new Map<string, number>();
				for (const r of records) {
					if (r.id != null && typeof r.rank === 'number') {
						rankById.set(r.id, r.rank);
					}
				}
				// `getRepositoryById` lives in repositoryEntries.ts (same `api`); types are split across injectEndpoints.
				const patchResult = dispatch(
					(api.util.updateQueryData as (
						endpointName: 'getRepositoryById',
						arg: string,
						updateRecipe: (draft: RepositoryByIdResponse) => void,
					) => ReturnType<typeof api.util.updateQueryData>)(
						'getRepositoryById',
						repositoryId,
						(draft: RepositoryByIdResponse) => {
							for (const def of draft.propertyDefinitions) {
								const rank = rankById.get(def.id);
								if (rank !== undefined) {
									(def as Entry).rank = rank;
								}
							}
						},
					),
				);

				dispatch(entriesUpdateMany(records));
				try {
					await queryFulfilled;
				} catch {
					patchResult.undo();
					return;
				}
				dispatch(api.util.invalidateTags([{type: 'Repository' as const, id: repositoryId}]));
			},
		}),
	}),
});

export const {
	usePropertyDefinitionCreateMutation,
	usePropertyDefinitionDeleteMutation,
	usePropertyDefinitionUpdateMutation,
	usePropertyDefinitionsUpdateManyMutation,
} = propertyDefinitionsApi;
